import { cn, Icon } from '@hibro/ui-client';
import { type ReactNode, useLayoutEffect, useRef } from 'react';

import { useTranslator } from '../../i18n/i18n';
import { WindowControls, type WindowControlsBridge } from './window-controls';

export type { WindowControlsBridge } from './window-controls';

/** Width reserved on the left for macOS native traffic lights (§5.8). */
const MAC_TRAFFIC_LIGHT_SPACING = 72;

export interface CustomHeaderProps {
  /**
   * Window-controls bridge (§5.8). When provided, the header draws the
   * minimize/maximize/close buttons on Windows/Linux and reserves space for the
   * native traffic lights on macOS. Omit it for a plain draggable bar.
   */
  bridge?: WindowControlsBridge;
  /** Window-centred slot (the workbench search field). Empty on chrome-only bars. */
  children?: ReactNode;
  /** Extra styles merged onto the bar (e.g. to tweak background per surface). */
  className?: string;
  leftContent?: ReactNode;
  rightContent?: ReactNode;
}

/**
 * Reusable custom title bar for frameless windows (架构方案 §5.7/§5.8). Three
 * equal-track columns keep `children` on the window midline: brand + optional
 * `leftContent` on the start, `children` in the auto middle, `rightContent` +
 * platform `WindowControls` on the end. The middle slot is capped to the clear
 * space shared by both sides, so a crowded title bar narrows its child without
 * moving it off-centre. Shared by the full `Workbench` header and the
 * single-surface `DetachedSurface` window so both get identical window chrome.
 */
export function CustomHeader({
  bridge,
  leftContent,
  children,
  rightContent,
  className,
}: CustomHeaderProps): ReactNode {
  // Only when the OS itself draws the buttons: the spacer exists to dodge the
  // real traffic lights, so a browser on macOS must not reserve it (见 18).
  const reserveNativeControls = bridge?.chrome === 'native';
  const { t } = useTranslator();
  // Reflect a non-stable release channel in the brand (见 01 渠道身份), e.g.
  // "HiBro Dev", so coexisting channels are distinguishable in-app too.
  const channelSuffix = channelLabel(bridge?.channel);
  const headerRef = useRef<HTMLElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const hasCenterContent = children !== undefined && children !== null;

  useLayoutEffect(() => {
    const header = headerRef.current;
    const left = leftRef.current;
    const centerSlot = centerRef.current;
    const right = rightRef.current;
    if (!centerSlot) {
      return;
    }
    if (!hasCenterContent) {
      centerSlot.style.removeProperty('width');
      return;
    }
    if (!header || !left || !right) {
      return;
    }

    const measure = (): void => {
      const headerRect = header.getBoundingClientRect();
      const leftBounds = horizontalBounds(left);
      const rightBounds = horizontalBounds(right);
      const midpoint = (headerRect.left + headerRect.right) / 2;
      const columnGap = Number.parseFloat(getComputedStyle(header).columnGap);
      const gap = Number.isFinite(columnGap) ? columnGap : 0;
      const freeHalfWidth = Math.max(
        0,
        Math.min(midpoint - leftBounds.right, rightBounds.left - midpoint) -
          gap,
      );
      centerSlot.style.width = `${Math.floor(freeHalfWidth * 2)}px`;
    };

    measure();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    observer.observe(left);
    observer.observe(right);
    for (const element of [...left.children, ...right.children]) {
      observer.observe(element);
    }
    return () => observer.disconnect();
  }, [hasCenterContent, leftContent, rightContent]);

  return (
    <header
      ref={headerRef}
      className={cn(
        'grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 h-9 px-3 bg-transparent',
        className,
      )}
      style={{
        // The whole bar is the OS drag region; interactive children opt out with
        // `WebkitAppRegion: 'no-drag'` (§5.8).
        WebkitAppRegion: 'drag',
      }}
    >
      <div
        ref={leftRef}
        className="flex min-w-0 items-center gap-3.5 justify-self-start overflow-hidden"
      >
        {reserveNativeControls ? (
          <div
            aria-hidden="true"
            style={{ width: MAC_TRAFFIC_LIGHT_SPACING + 'px' }}
          />
        ) : null}
        <div className="flex min-w-0 items-center gap-1">
          <Icon
            className="size-5.5 shrink-0"
            spec={{ type: 'builtin', id: 'logo' }}
          />
          <div className="truncate text-xs text-foreground">
            {t('workbench.brand')}
            {channelSuffix ? ` ${channelSuffix}` : ''}
          </div>
        </div>
        {leftContent ? leftContent : null}
      </div>
      <div
        ref={centerRef}
        className="flex min-w-0 max-w-105 justify-center justify-self-center min-w-[220px]"
      >
        {children}
      </div>
      <div
        ref={rightRef}
        className="flex min-w-0 items-center justify-end justify-self-end"
      >
        {rightContent ? rightContent : null}
        {bridge ? <WindowControls bridge={bridge} /> : null}
      </div>
    </header>
  );
}

/** Visible horizontal extent, including descendants that overflow their grid item. */
function horizontalBounds(element: HTMLElement): {
  left: number;
  right: number;
} {
  const own = element.getBoundingClientRect();
  let left = own.left;
  let right = own.right;
  for (const descendant of element.querySelectorAll('*')) {
    // Base UI adds 1px focus guards at (-1, -1) when a menu opens. They are
    // keyboard infrastructure, not visible title-bar content: counting them
    // makes the right cluster cross the midpoint and collapses the search.
    if (descendant.hasAttribute('data-base-ui-focus-guard')) {
      continue;
    }
    const rect = descendant.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      continue;
    }
    left = Math.min(left, rect.left);
    right = Math.max(right, rect.right);
  }
  return { left, right };
}

/**
 * Brand suffix for a release channel (见 01 渠道身份): `stable` (or unknown) gets
 * no suffix; other channels title-case each `-` segment (e.g. `dev` -> `Dev`).
 */
function channelLabel(channel?: string): string {
  if (!channel || channel === 'stable') {
    return '';
  }
  return channel
    .split('-')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');
}
